import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Button,
  Tag,
  Badge,
  Spin,
  Divider,
  Select,
  Space,
  Typography,
  Card,
  Tabs,
  Tooltip,
  message,
  Modal,
  Form,
} from 'antd';
import {
  CloseOutlined,
  ArrowRightOutlined,
  SyncOutlined,
  EyeOutlined,
  PlusOutlined,
  MinusOutlined,
  EditOutlined,
  LinkOutlined,
  UserOutlined,
  LoadingOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { useUserContext } from '../../../../core/components/users/useUserContext';
import { useOrgContext } from '../../../../core/components/orgs/useOrgContext';
import { statusTypes, urgencyColors } from '../configs/task.config';
import AvatarS3 from '../../../shared/images/AvatarS3';
import { TaskComments } from './TaskComments';
import { Histories } from '../../../shared/Histories';
import { debounce, startCase } from 'lodash';
import { copyTaskLink } from '../utils/task.utils';
import { useWindowWidth } from '../../../../core/hooks/useWindowWidth';

const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

const TaskDrawerFull = ({ visible, onClose, taskId }) => {
  const {
    currentUser,
    fetchTask,
    currentTask,
    updateTask,
    updateTaskLoading,
    refetchTask,
    fetchHistories,
    histories,
    loadingHistories,
  } = useTaskContext();
  const {
    fetchBasicUsersDetails,
    basicUsersDetails,
    basicUsersDetailsLoading,
    fetchCollaboratorsUsers,
    collaboratorsData,
    collaboratorsLoading,
  } = useUserContext();
  const { fetchBasicOrgsDetails, basicOrgsDetails, basicOrgsDetailsLoading } = useOrgContext();

  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('1');
  const [isEditingAssignee, setIsEditingAssignee] = useState(false);
  const [isFollowers, setIsFollowers] = useState(false);
  const [isCollaborator, setIsCollaborator] = useState(false);
  const [showFollowerModal, setShowFollowerModal] = useState(false);
  const [selectedFollowerId, setSelectedFollowerId] = useState(null);
  const windowWidth = useWindowWidth();

  const level =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.level;

  const isCurrentUserAssignee =
    currentTask && currentTask.assigneeUsers
      ? currentTask.assigneeUsers.some((user) => user.id === currentUser.id)
      : false;

  // Fetch functions
  const fetchBasicUsers = debounce((name = '') => {
    const selectedOrg = form.getFieldValue('assigneeOrg');
    if (selectedOrg === null) {
      form.setFieldValue('assigneeUsers', []);
      return;
    }
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  id: selectedOrg,
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const fetchCollaborators = debounce((name = '') => {
    fetchCollaboratorsUsers({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  OR: [
                    {
                      parentOrg_some: {
                        id: currentUser.role.org.id,
                      },
                    },
                    {
                      id: currentUser.role.org.id,
                    },
                  ],
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const fetchBasicOrgs = debounce((name = '') => {
    fetchBasicOrgsDetails({
      variables: {
        first: 10,
        skip: 0,
        where: {
          AND: [
            {
              OR: [
                { parentOrg_some: { id: currentUser.role.org.id } },
                { id: currentUser.role.org.id },
              ],
              name_contains: name,
            },
          ],
          OR: [
            {
              roles_some: {
                org: {
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
            },
          ],
        },
      },
    });
  }, 500);

  useEffect(() => {
    if (visible && taskId) {
      fetchTask({
        variables: { where: { id: taskId } },
      });
      fetchCollaborators();
    } else if (!visible) {
      setIsEditingAssignee(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, taskId]);

  useEffect(() => {
    if (currentTask) {
      setIsFollowers(
        currentTask.followers &&
          currentUser &&
          currentTask.followers.some((f) => f.id === currentUser.id)
      );
      setIsCollaborator(
        currentTask &&
          currentTask.collaborators &&
          currentUser &&
          currentTask.collaborators.some((c) => c.id === currentUser.id)
      );

      // Set form values
      form.setFieldsValue({
        status: currentTask.status,
        assigneeOrg: currentTask.assigneeOrg?.id || null,
        assigneeUsers:
          currentTask.assigneeUsers && currentTask.assigneeUsers[0]
            ? currentTask.assigneeUsers[0].id
            : undefined,
        collaborators:
          currentTask.collaborators && currentTask.collaborators.length > 0
            ? currentTask.collaborators.map((user) => user.id)
            : [],
      });
    }
  }, [currentTask, currentUser, form]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === '2' && taskId) {
      fetchHistories({
        variables: {
          where: {
            referenceId: taskId,
            type: 'TASK',
          },
        },
      });
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateTask({
        variables: {
          data: { status: newStatus },
          where: { id: taskId },
        },
      });
      await fetchTask({
        variables: { where: { id: taskId } },
      });
      message.success('Task status updated successfully.');
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Failed to update task status.');
    }
  };

  const handleFollowToggle = async () => {
    const createdBy = currentUser.id === (currentTask?.createdBy?.id);

    if (!isFollowers && (isCurrentUserAssignee || isCollaborator || createdBy)) {
      if (isCurrentUserAssignee) {
        message.warning('Assignees already follow tasks by default.');
      } else if (isCollaborator) {
        message.warning('Collaborators already follow tasks by default.');
      } else if (createdBy) {
        message.warning('You are the creator of this task and already follows it by default.');
      }
      return;
    }

    setIsFollowers(!isFollowers);
    try {
      await updateTask({
        variables: {
          data: {
            followers: !isFollowers
              ? { connect: { id: currentUser.id } }
              : { disconnect: { id: currentUser.id } },
          },
          where: { id: taskId },
        },
      });
      await refetchTask();
      message.success(!isFollowers ? 'You followed the task.' : 'Unfollowed task.');
    } catch (error) {
      message.error(!isFollowers ? 'Failed to follow.' : 'Failed to unfollow task.');
    }
  };

  const handleCollaboratorToggle = async () => {
    if (!isCollaborator && (isCurrentUserAssignee || isFollowers)) {
      if (isCurrentUserAssignee) {
        message.warning('Assignees are already collaborators but with more responsibility.');
      } else if (isFollowers) {
        message.warning('Please unfollow first if you want to be a collaborator.');
      }
      return;
    }

    setIsCollaborator(!isCollaborator);
    try {
      await updateTask({
        variables: {
          data: {
            collaborators: !isCollaborator
              ? { connect: { id: currentUser.id } }
              : { disconnect: { id: currentUser.id } },
          },
          where: { id: taskId },
        },
      });
      await refetchTask();
      message.success(!isCollaborator ? 'You are now a collaborator of the task.' : 'Left the task.');
    } catch (error) {
      message.error(!isCollaborator ? 'Failed to be a collaborator.' : 'Failed to leave the task.');
    }
  };

  const handleCopyLink = () => {
    copyTaskLink(taskId, message);
  };

  const handleUserUpdate = async () => {
    const values = form.getFieldsValue();
    try {
      const orgUpdate = values.assigneeOrg
        ? { connect: { id: values.assigneeOrg } }
        : currentTask && currentTask.assigneeOrg
          ? { disconnect: true }
          : {};

      const newAssignee = values.assigneeUsers;
      const newCollaborators = values.collaborators || [];

      const currentAssignees = currentTask?.assigneeUsers || [];
      const currentAssigneeIds = currentAssignees.map((user) => user.id);
      const previousCollaborators = currentTask?.collaborators || [];

      const assigneeUsers = {};
      const collaborators = {};

      if (
        previousCollaborators.some((c) => c.id === newAssignee) ||
        newCollaborators.includes(newAssignee)
      ) {
        message.warning(
          'Assignees are already collaborators. Please remove them from collaborators first.'
        );
        return;
      }

      if (currentAssignees.length > 0) {
        const disconnectIds = currentAssigneeIds.filter((id) => id !== newAssignee);
        if (disconnectIds.length > 0) {
          assigneeUsers.disconnect = disconnectIds.map((id) => ({ id }));
        }
      }

      if (newAssignee && !currentAssigneeIds.includes(newAssignee)) {
        assigneeUsers.connect = { id: newAssignee };
      }

      const usersToConnect = newCollaborators.filter(
        (id) => !previousCollaborators.some((user) => user.id === id)
      );

      const usersToDisconnect = previousCollaborators.filter(
        (user) => !newCollaborators.includes(user.id)
      );

      if (usersToDisconnect.length > 0) {
        collaborators.disconnect = usersToDisconnect.map((user) => ({ id: user.id }));
      }

      if (usersToConnect.length > 0) {
        collaborators.connect = usersToConnect.map((id) => ({ id }));
      }

      await updateTask({
        variables: {
          data: {
            assigneeOrg: orgUpdate,
            assigneeUsers,
            collaborators,
            activityLog: 'Updated Assignee',
          },
          where: { id: taskId },
        },
      });

      await refetchTask();
      message.success('Task assignee updated successfully.');
      setIsEditingAssignee(false);
    } catch (error) {
      message.error('Failed to update task assignee.');
      console.log('Update failed:', error);
    }
  };

  if (!visible) {
    return null;
  }

  if (!currentTask) {
    return (
      <Drawer
        title="Loading Task..."
        placement="right"
        width={windowWidth < 768 ? '100%' : windowWidth < 1024 ? '90%' : '70%'}
        onClose={onClose}
        open={visible}
        maskClosable={true}
      >
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" tip="Loading task details..." />
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      title={null}
      placement="right"
      width={windowWidth < 768 ? '100%' : windowWidth < 1024 ? '90%' : '70%'}
      onClose={onClose}
      open={visible}
      maskClosable={true}
      closeIcon={<CloseOutlined style={{ fontSize: 16 }} />}
      styles={{
        header: {
          borderBottom: 'none',
          padding: windowWidth < 768 ? '16px 16px 0' : '32px 32px 0',
        },
        body: {
          padding: windowWidth < 768 ? '16px' : '24px',
          overflowY: 'auto',
        },
      }}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: windowWidth < 768 ? '12px 0' : '16px 0',
          }}
        >
          <Link to={`/tasks/details/${taskId}`}>
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              size={windowWidth < 768 ? 'middle' : 'large'}
              style={{ width: windowWidth < 768 ? '100%' : 'auto' }}
            >
              View Full Page
            </Button>
          </Link>
        </div>
      }
    >
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            alignItems: windowWidth < 768 ? 'stretch' : 'flex-start',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1 }}>
            <Badge
              dot
              color={urgencyColors[currentTask.urgency] || '#d9d9d9'}
              style={{ marginTop: 8 }}
            />
            <div style={{ flex: 1 }}>
              <Text
                strong
                style={{
                  fontSize: windowWidth < 768 ? 16 : 18,
                  display: 'block',
                  lineHeight: 1.4,
                }}
              >
                {currentTask.name}
              </Text>
              {currentTask.project && (
                <div style={{ marginTop: 8 }}>
                  <Link to={`/tasks/projects/details/${currentTask.project.id}`}>
                    <Tag
                      color="blue"
                      style={{
                        fontSize: '13px',
                        padding: '4px 12px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                      }}
                    >
                      <LinkOutlined style={{ marginRight: 4 }} />
                      Project: {currentTask.project.name}
                    </Tag>
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: windowWidth < 768 ? 'column' : 'row',
              gap: 8,
              alignItems: 'stretch',
            }}
          >
            {level !== 'CHANNEL_PARTNER' && (
              <Select
                className="custom-select"
                style={{
                  width: windowWidth < 768 ? '100%' : '180px',
                  height: '45px',
                  borderRadius: '5px',
                  paddingTop: '2px',
                }}
                value={currentTask.status}
                placeholder="Update Status"
                onChange={handleStatusChange}
                loading={updateTaskLoading}
                disabled={updateTaskLoading}
              >
                {Object.keys(statusTypes).map((status) => (
                  <Option key={statusTypes[status]} value={statusTypes[status]}>
                    {status}
                  </Option>
                ))}
              </Select>
            )}
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: windowWidth < 768 ? 'stretch' : 'flex-start',
              }}
            >
              <Tooltip title="Copy Task Link">
                <Button
                  onClick={handleCopyLink}
                  icon={<CopyOutlined />}
                  style={{
                    fontSize: '16px',
                    height: '41px',
                    width: '41px',
                    borderRadius: '5px',
                  }}
                />
              </Tooltip>
              <Link to={`/tasks/form/${taskId}`} style={{ flex: windowWidth < 768 ? 1 : 0 }}>
                <Button
                  style={{
                    fontSize: '15px',
                    height: '41px',
                    borderRadius: '5px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    width: windowWidth < 768 ? '100%' : '130px',
                  }}
                >
                  <EditOutlined /> Edit Task
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{
          display: 'flex',
          flexDirection: windowWidth < 1024 ? 'column-reverse' : 'row',
          gap: windowWidth < 768 ? 16 : 32,
        }}
      >
        {/* Left: Tabs Content */}
        <div style={{ flex: 1 }}>
          <Tabs activeKey={activeTab} onChange={handleTabChange} type="card">
            <TabPane tab="Details" key="1">
              <div style={{ fontSize: 14 }}>
                <TaskComments currentTask={currentTask} currentTaskId={taskId} />
              </div>
            </TabPane>
            <TabPane tab="History" key="2">
              <div style={{ fontSize: 14 }}>
                <Histories histories={histories} loading={loadingHistories} />
              </div>
            </TabPane>
          </Tabs>
        </div>

        {/* Right: Sidebar */}
        <div
          style={{
            width: windowWidth < 1024 ? '100%' : 360,
            flexShrink: 0,
          }}
        >
          {/* Followers Card */}
          <Card
            size="small"
            style={{
              marginBottom: 20,
              padding: '8px',
              border: '1px solid #e5edf4',
              borderRadius: '8px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <Space>
                <EyeOutlined style={{ color: '#3b82f6' }} />
                <Text strong>Followers</Text>
              </Space>
              <Button
                type="text"
                size="small"
                onClick={handleFollowToggle}
                icon={isFollowers ? <MinusOutlined /> : <PlusOutlined />}
                style={{ color: isFollowers ? '#F38181' : '#3b82f6' }}
              >
                {isFollowers ? 'Unfollow' : 'Follow'}
              </Button>
            </div>
            {currentTask.followers && currentTask.followers.length > 0 ? (
              <Space wrap size={[8, 8]}>
                {currentTask.followers.map((follower) => (
                  <Tooltip key={follower.id} title={startCase(follower.name || follower.username)}>
                    <AvatarS3
                      imgKey={follower.avatar?.key}
                      username={follower.name || follower.username}
                      avatarProps={{ size: 32 }}
                    />
                  </Tooltip>
                ))}
              </Space>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                No followers yet
              </Text>
            )}
          </Card>

          {/* Details Card */}
          <Card
            size="small"
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>Details</Text>
                {currentTask.linkUrl && (
                  <LinkOutlined
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const url = currentTask.linkUrl;
                      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                        window.open(url, '_blank');
                      }
                    }}
                  />
                )}
              </div>
            }
            style={{
              marginBottom: 20,
              padding: '8px',
              border: '1px solid #e5edf4',
              borderRadius: '8px',
            }}
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Urgency
                </Text>
                <br />
                {currentTask.urgency ? (
                  <Tag
                    style={{
                      backgroundColor: urgencyColors[currentTask.urgency],
                      borderRadius: '25px',
                      textAlign: 'center',
                      border: 0,
                      width: '100px',
                      padding: '2px',
                      opacity: 0.7,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                      <div
                        style={{
                          width: '5px',
                          height: '5px',
                          borderRadius: '50px',
                          backgroundColor: currentTask.urgency === 'MEDIUM' ? '#1C1C2D' : 'white',
                        }}
                      />
                      <Text
                        strong
                        style={{
                          fontSize: 12,
                          color: currentTask.urgency === 'MEDIUM' ? '#1C1C2D' : 'white',
                        }}
                      >
                        {startCase(currentTask.urgency.toLowerCase())}
                      </Text>
                    </div>
                  </Tag>
                ) : (
                  <Text style={{ fontSize: 13, color: '#8c8c8c' }}>-</Text>
                )}
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Start Date
                </Text>
                <br />
                <Text style={{ fontSize: 13 }}>
                  {currentTask.startDate
                    ? new Date(currentTask.startDate).toLocaleDateString()
                    : '-'}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Due Date
                </Text>
                <br />
                <Text style={{ fontSize: 13 }}>
                  {currentTask.dueDate
                    ? new Date(currentTask.dueDate).toLocaleDateString()
                    : '-'}
                </Text>
                {currentTask.recurring && (
                  <SyncOutlined style={{ marginLeft: 8, color: '#8c8c8c' }} />
                )}
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Created On
                </Text>
                <br />
                <Text style={{ fontSize: 13 }}>
                  {currentTask.createdAt
                    ? new Date(currentTask.createdAt).toLocaleDateString()
                    : 'N/A'}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Requested By
                </Text>
                <br />
                <Text style={{ fontSize: 13 }}>
                  {startCase(currentTask.createdBy?.name || currentTask.createdBy?.username || 'Unknown')}
                </Text>
              </div>
              {currentTask.channelPartner && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Channel Partner
                  </Text>
                  <br />
                  <Text style={{ fontSize: 13 }}>{currentTask.channelPartner.name}</Text>
                </div>
              )}
              {currentTask.advertiser && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Advertiser
                  </Text>
                  <br />
                  <Tag color="geekblue">{currentTask.advertiser.name}</Tag>
                </div>
              )}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Request Type
                </Text>
                <br />
                <Text style={{ fontSize: 13 }}>{currentTask.requestType || '-'}</Text>
              </div>
              {currentTask.requestSubType && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Request Details
                  </Text>
                  <br />
                  <Text style={{ fontSize: 13 }}>{currentTask.requestSubType}</Text>
                </div>
              )}
            </Space>
          </Card>

          {/* Assignees & Collaborators Card */}
          <Card
            size="small"
            style={{
              marginBottom: 20,
              padding: '8px',
              border: '1px solid #e5edf4',
              borderRadius: '8px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <Text strong>Assigned to</Text>
              {!isEditingAssignee && level === 'PLATFORM' && (
                <EditOutlined
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setIsEditingAssignee(true);
                    fetchBasicOrgs();
                  }}
                />
              )}
            </div>

            <Form form={form} layout="vertical">
              {!currentTask?.project && (
                <Form.Item label="Assignee Org" name="assigneeOrg" style={{ marginBottom: 12 }}>
                  {isEditingAssignee ? (
                    <Select
                      placeholder="Select Organization"
                      loading={basicOrgsDetailsLoading}
                      showSearch
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA, optionB) =>
                        optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                      }
                      onChange={() => {
                        form.setFieldValue('assigneeUsers', undefined);
                        fetchBasicUsers();
                      }}
                    >
                      {basicOrgsDetails
                        .filter((org) => org.level === 'PLATFORM')
                        .concat(
                          currentTask?.assigneeOrg &&
                            currentTask.assigneeOrg.level === 'PLATFORM' &&
                            !basicOrgsDetails.some((org) => org.id === currentTask.assigneeOrg.id)
                            ? [currentTask.assigneeOrg]
                            : []
                        )
                        .map((org) => (
                          <Option key={org.id} value={org.id}>
                            {org.name}
                          </Option>
                        ))}
                    </Select>
                  ) : (
                    <Text style={{ fontSize: 13, color: '#8c8c8c' }}>
                      {currentTask.assigneeOrg?.name || '-'}
                    </Text>
                  )}
                </Form.Item>
              )}

              <Form.Item
                label={
                  <span>
                    Assignee
                    {currentTask?.project && (
                      <Text style={{ fontSize: 12, color: '#64748b', fontWeight: 'normal', marginLeft: 8 }}>
                        (from project team)
                      </Text>
                    )}
                  </span>
                }
                name="assigneeUsers"
                style={{ marginBottom: 12 }}
              >
                {isEditingAssignee ? (
                  <Select
                    placeholder={
                      currentTask?.project
                        ? 'Select from project team'
                        : form.getFieldValue('assigneeOrg')
                        ? 'Select Assignee'
                        : 'Select an organization first'
                    }
                    onSearch={(e) => !currentTask?.project && fetchBasicUsers(e)}
                    onFocus={() => !currentTask?.project && fetchBasicUsers()}
                    loading={basicUsersDetailsLoading}
                    filterOption={currentTask?.project}
                    filterSort={(optionA, optionB) => {
                      return optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase());
                    }}
                    onChange={(selectedId) => {
                      const followers = currentTask.followers || [];
                      const followerIds = followers.map((user) => user.id);
                      if (followerIds.includes(selectedId)) {
                        setShowFollowerModal(true);
                        setSelectedFollowerId(selectedId);
                      }
                    }}
                  >
                    {currentTask?.project ? (
                      <>
                        {currentTask.project.owner && (
                          <Option key={currentTask.project.owner.id} value={currentTask.project.owner.id}>
                            {startCase(currentTask.project.owner.name || currentTask.project.owner.username)}
                          </Option>
                        )}
                        {currentTask.project.team && currentTask.project.team.map((user) => (
                          <Option key={user.id} value={user.id}>
                            {startCase(user.name || user.username)}
                          </Option>
                        ))}
                      </>
                    ) : (
                      basicUsersDetails.map((user) => (
                        <Option key={user.id} value={user.id}>
                          {startCase(user.name || user.username)}
                        </Option>
                      ))
                    )}
                  </Select>
                ) : (
                  <div>
                    {currentTask.assigneeUsers && currentTask.assigneeUsers.length > 0 ? (
                      currentTask.assigneeUsers.map((user) => (
                        <div key={user.id} style={{ marginBottom: 4 }}>
                          <Text style={{ fontSize: 13, color: '#8c8c8c' }}>
                            {startCase(user.name || user.username)}
                          </Text>
                        </div>
                      ))
                    ) : (
                      <Text style={{ fontSize: 13, color: '#8c8c8c' }}>-</Text>
                    )}
                  </div>
                )}
              </Form.Item>
            </Form>

            <Divider style={{ margin: '16px 0' }} />

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <Space>
                <UserOutlined style={{ color: '#22c55e' }} />
                <Text strong>Collaborators</Text>
              </Space>
              {level === 'PLATFORM' && !isEditingAssignee && (
                <Button
                  type="text"
                  size="small"
                  onClick={handleCollaboratorToggle}
                  icon={isCollaborator ? <MinusOutlined /> : <PlusOutlined />}
                  style={{ color: isCollaborator ? '#F38181' : '#22c55e' }}
                >
                  {isCollaborator ? 'Leave' : 'Join'}
                </Button>
              )}
            </div>

            {!isEditingAssignee ? (
              <>
                {currentTask.collaborators && currentTask.collaborators.length > 0 ? (
                  <Space wrap size={[8, 8]}>
                    {currentTask.collaborators.map((collaborator) => (
                      <Tooltip
                        key={collaborator.id}
                        title={startCase(collaborator.name || collaborator.username)}
                      >
                        <AvatarS3
                          imgKey={collaborator.avatar?.key}
                          username={collaborator.name || collaborator.username}
                          avatarProps={{ size: 32 }}
                        />
                      </Tooltip>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    No collaborators yet
                  </Text>
                )}
              </>
            ) : (
              <>
                <Form form={form} layout="vertical">
                  <Form.Item name="collaborators" style={{ marginBottom: 12 }}>
                    <Select
                      mode="multiple"
                      placeholder="Select collaborator(s)"
                      onSearch={fetchCollaborators}
                      onFocus={() => fetchCollaborators()}
                      loading={collaboratorsLoading}
                      filterOption={false}
                      filterSort={(optionA, optionB) => {
                        return optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase());
                      }}
                    >
                      {(collaboratorsData || []).map((user) => (
                        <Option key={user.id} value={user.id}>
                          {startCase(user.name || user.username)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Form>
                <Divider style={{ margin: '16px 0' }} />
                <div style={{ marginTop: '10px' }}>
                  <Button
                    type="primary"
                    onClick={handleUserUpdate}
                    disabled={updateTaskLoading}
                    size="small"
                    style={{
                      marginLeft: '10px',
                      fontSize: '13px',
                      height: '30px',
                      borderRadius: '5px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                    }}
                  >
                    {updateTaskLoading && <LoadingOutlined />} Save
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditingAssignee(false);
                      // Reset form values
                      form.setFieldsValue({
                        assigneeOrg: currentTask.assigneeOrg?.id || null,
                        assigneeUsers:
                          currentTask.assigneeUsers && currentTask.assigneeUsers[0]
                            ? currentTask.assigneeUsers[0].id
                            : undefined,
                        collaborators:
                          currentTask.collaborators && currentTask.collaborators.length > 0
                            ? currentTask.collaborators.map((user) => user.id)
                            : [],
                      });
                    }}
                    size="small"
                    style={{
                      marginLeft: '10px',
                      fontSize: '13px',
                      height: '30px',
                      borderRadius: '5px',
                      color: 'black',
                    }}
                    disabled={updateTaskLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>

      {/* Follower Warning Modal */}
      {showFollowerModal && (
        <Modal
          centered
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                form.setFieldValue('assigneeUsers', undefined);
                setShowFollowerModal(false);
              }}
              disabled={updateTaskLoading}
            >
              Cancel
            </Button>,
            <Button
              key="ok"
              type="primary"
              disabled={updateTaskLoading}
              onClick={async () => {
                try {
                  message.loading('Removing follower...', 0);
                  await updateTask({
                    variables: {
                      where: { id: taskId },
                      data: {
                        followers: {
                          disconnect: [{ id: selectedFollowerId }],
                        },
                      },
                    },
                  });
                  await refetchTask();
                  message.destroy();
                  message.success('Follower removed successfully');
                  form.setFieldValue('assigneeUsers', selectedFollowerId);
                  setShowFollowerModal(false);
                } catch (error) {
                  message.destroy();
                  message.error(`Failed to remove follower: ${error.message}`);
                }
              }}
            >
              {updateTaskLoading && <LoadingOutlined />} Remove
            </Button>,
          ]}
          title="Remove Follower"
          open={showFollowerModal}
        >
          <p>
            This user is a follower. Do you want to promote them as an assignee and remove them from
            followers?
          </p>
        </Modal>
      )}
    </Drawer>
  );
};

export default TaskDrawerFull;
